// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — buying a plan online through Paddle, the merchant of record
 * (`docs/AYNI_XR_LICENSING.md` "Online payment"). Sold online: Plus,
 * Teacher, School, Researcher and Lab (`billingCatalog.ts`).
 *
 * The page knows Paddle's client-side token and the prices from build
 * configuration: `VITE_PADDLE_CLIENT_TOKEN`, `VITE_PADDLE_ENV`, and
 * `VITE_PADDLE_PRICES`, a JSON map from a price key (`plus/month`,
 * `teacher/year`, `researcher-commercial/year`, …; `billingCatalog.priceKeysFor`)
 * to a Paddle price id. The token is public by design; no secret is ever in
 * the page. A plan is sold online only when every price it needs is in the
 * map (`planOnline`); otherwise its card keeps the invoice request.
 *
 * - `previewPrices` / `previewChoice`: what Paddle will charge this visitor,
 *   with their country's VAT, formatted by Paddle (never reformatted).
 * - `startCheckout`: asks the site for a checkout reference
 *   (`/api/billing/checkout`, signed-in accounts only), then opens Paddle's
 *   overlay checkout with it as custom data, the account's email filled in.
 *   The licence follows from Paddle's webhook, never from this page.
 * - `openBillingPortal`: Paddle's billing portal (cancel, card, invoices),
 *   a fresh link from `/api/billing/portal` on every click.
 *
 * Paddle.js is loaded only when the plans need it.
 */

import type { Paddle, PaddleEventData } from '@paddle/paddle-js'
import { checkoutLines, priceKeysFor, type OnlinePlanId, type PlanChoice } from './billingCatalog'

export type { BillingInterval, PlanChoice } from './billingCatalog'

interface PaddleConfig {
  token: string
  environment: 'sandbox' | 'production'
  /** Price key → Paddle price id. */
  prices: Record<string, string>
}

function readConfig(): PaddleConfig | null {
  const env = import.meta.env
  const token = env.VITE_PADDLE_CLIENT_TOKEN?.trim()
  if (!token) return null
  let prices: Record<string, string> = {}
  try {
    const parsed = JSON.parse(env.VITE_PADDLE_PRICES?.trim() || '{}') as unknown
    if (parsed && typeof parsed === 'object') {
      prices = Object.fromEntries(Object.entries(parsed as Record<string, unknown>).filter((e): e is [string, string] => typeof e[1] === 'string' && /^pri_[a-z0-9]{26}$/.test(e[1])))
    }
  } catch {
    return null
  }
  if (Object.keys(prices).length === 0) return null
  return { token, environment: env.VITE_PADDLE_ENV?.trim() === 'production' ? 'production' : 'sandbox', prices }
}

let config: PaddleConfig | null | undefined

function paddleConfig(): PaddleConfig | null {
  if (config === undefined) config = readConfig()
  return config
}

/** Whether this build can sell anything online. */
export function billingAvailable(): boolean {
  return paddleConfig() !== null
}

/** Whether this build sells a plan online: every price it needs is configured. */
export function planOnline(plan: string): plan is OnlinePlanId {
  const cfg = paddleConfig()
  if (!cfg) return false
  try {
    return priceKeysFor(plan as OnlinePlanId).every((key) => key in cfg.prices)
  } catch {
    return false
  }
}

/** The Paddle price for a price key (`billingCatalog.planPriceKey`, …). */
export function priceIdFor(key: string): string | null {
  return paddleConfig()?.prices[key] ?? null
}

// ------------------------------------------------------------- Paddle.js

type EventListener = (event: PaddleEventData) => void
let listener: EventListener | null = null
let loading: Promise<Paddle | null> | null = null

/** Paddle.js, initialised once; null when it cannot load (offline, blocked, not configured). */
function paddle(): Promise<Paddle | null> {
  const cfg = paddleConfig()
  if (!cfg) return Promise.resolve(null)
  loading ??= import('@paddle/paddle-js')
    .then(({ initializePaddle }) =>
      initializePaddle({ token: cfg.token, environment: cfg.environment, eventCallback: (event) => listener?.(event) }),
    )
    .then((p) => p ?? null)
    .catch(() => {
      loading = null
      return null
    })
  return loading
}

/** Price id → what Paddle will charge this visitor for one of it, VAT included, formatted by Paddle. */
export type PricePreview = Record<string, string>

let previewed: Promise<PricePreview | null> | null = null

/** Every configured price for this visitor's country (from their address), or null when Paddle cannot say. */
export function previewPrices(): Promise<PricePreview | null> {
  const cfg = paddleConfig()
  if (!cfg) return Promise.resolve(null)
  previewed ??= paddle()
    .then(async (p) => {
      if (!p) return null
      const ids = [...new Set(Object.values(cfg.prices))]
      const res = await p.PricePreview({ items: ids.map((priceId) => ({ priceId, quantity: 1 })) })
      const out: PricePreview = {}
      for (const item of res.data.details.lineItems) out[item.price.id] = item.formattedTotals.total
      return out
    })
    .catch(() => {
      previewed = null
      return null
    })
  return previewed
}

/** What Paddle will charge this visitor for a choice, line by line, VAT included; null when it cannot say. */
export async function previewChoice(choice: PlanChoice): Promise<string[] | null> {
  const items = checkoutLines(choice).map((l) => ({ priceId: priceIdFor(l.key), quantity: l.quantity }))
  if (items.some((i) => !i.priceId)) return null
  const p = await paddle()
  if (!p) return null
  try {
    const res = await p.PricePreview({ items: items as { priceId: string; quantity: number }[] })
    return res.data.details.lineItems.map((item) => item.formattedTotals.total)
  } catch {
    return null
  }
}

// --------------------------------------------------------------- checkout

export type CheckoutError = 'signed_out' | 'already_subscribed' | 'has_licence' | 'too_many_licences' | 'too_many' | 'academic_email' | 'unavailable' | 'unreachable'

export interface CheckoutHandlers {
  /** Paddle's overlay is open. */
  onOpened?(): void
  /** Payment went through. The licence follows from the webhook. */
  onCompleted(): void
  /** The buyer closed the overlay without paying. */
  onClosed?(): void
}

async function post(path: string, body: unknown, fetchImpl: typeof fetch): Promise<{ status: number; body: Record<string, unknown> } | null> {
  try {
    const res = await fetchImpl(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
      credentials: 'same-origin',
    })
    return { status: res.status, body: (await res.json().catch(() => ({}))) as Record<string, unknown> }
  } catch {
    return null
  }
}

const CHECKOUT_ERRORS: readonly CheckoutError[] = ['signed_out', 'already_subscribed', 'has_licence', 'too_many_licences', 'too_many', 'academic_email']

/**
 * Buy a plan: a checkout reference from the site, then Paddle's overlay.
 * Resolves once the overlay is asked to open, or with why it could not.
 */
export async function startCheckout(choice: PlanChoice, locale: string, handlers: CheckoutHandlers, fetchImpl: typeof fetch = fetch): Promise<{ ok: true } | { ok: false; error: CheckoutError }> {
  const items = checkoutLines(choice).map((l) => ({ priceId: priceIdFor(l.key), quantity: l.quantity }))
  if (items.length === 0 || items.some((i) => !i.priceId)) return { ok: false, error: 'unavailable' }
  const reply = await post('/api/billing/checkout', choice, fetchImpl)
  if (!reply) return { ok: false, error: 'unreachable' }
  if (reply.status !== 200) {
    const error = reply.body.error as CheckoutError
    return { ok: false, error: CHECKOUT_ERRORS.includes(error) ? error : reply.status === 503 ? 'unavailable' : 'unreachable' }
  }
  const customData = reply.body.customData as Record<string, string> | undefined
  const email = typeof reply.body.email === 'string' ? reply.body.email : undefined
  const p = await paddle()
  if (!p || !customData) return { ok: false, error: 'unreachable' }
  let completed = false
  listener = (event) => {
    const name = String(event.name ?? '')
    if (name === 'checkout.loaded') handlers.onOpened?.()
    else if (name === 'checkout.completed') {
      completed = true
      handlers.onCompleted()
    } else if (name === 'checkout.closed') {
      listener = null
      if (!completed) handlers.onClosed?.()
    }
  }
  p.Checkout.open({
    items: items as { priceId: string; quantity: number }[],
    customData,
    ...(email ? { customer: { email } } : {}),
    settings: { displayMode: 'overlay', variant: 'one-page', locale: locale.split('-')[0], allowLogout: false },
  })
  return { ok: true }
}

// ----------------------------------------------------------- payment links

const TRANSACTION_ID = /^txn_[a-z0-9]{26}$/

/**
 * The Paddle payment a payment link asks for (`?_ptxn=txn_…`: Paddle's
 * emails to update a card after a failed renewal, or a payment sent from
 * Paddle's dashboard), taken out of the address bar so a reload does not
 * open it again; null when the address has none.
 */
export function takePaymentLink(win: Pick<Window, 'location' | 'history'> | null = typeof window === 'undefined' ? null : window): string | null {
  if (!win) return null
  const url = new URL(win.location.href)
  const txn = url.searchParams.get('_ptxn')
  if (txn === null) return null
  url.searchParams.delete('_ptxn')
  try {
    win.history.replaceState(win.history.state, '', url.pathname + url.search + url.hash)
  } catch {
    // A sandboxed page: the parameter stays; Paddle.js is opened explicitly below anyway.
  }
  return TRANSACTION_ID.test(txn) ? txn : null
}

/**
 * Open Paddle's checkout for a payment link's transaction. No account is
 * needed: the payment already belongs to its buyer at Paddle. The licence
 * follows from the webhook, as for any payment.
 */
export async function openPaymentLink(transactionId: string, locale: string, handlers: Partial<CheckoutHandlers> = {}): Promise<boolean> {
  if (!TRANSACTION_ID.test(transactionId)) return false
  const p = await paddle()
  if (!p) return false
  let completed = false
  listener = (event) => {
    const name = String(event.name ?? '')
    if (name === 'checkout.completed') {
      completed = true
      handlers.onCompleted?.()
    } else if (name === 'checkout.closed') {
      listener = null
      if (!completed) handlers.onClosed?.()
    }
  }
  p.Checkout.open({ transactionId, settings: { displayMode: 'overlay', variant: 'one-page', locale: locale.split('-')[0] } })
  return true
}

// ------------------------------------------------------------------ portal

/**
 * Open Paddle's billing portal in a new tab. The tab is opened before the
 * link is asked for, so a popup blocker lets it through.
 */
export async function openBillingPortal(fetchImpl: typeof fetch = fetch, opener: (url: string) => Window | null = (url) => window.open(url, '_blank')): Promise<'opened' | 'no_subscription' | 'unavailable' | 'unreachable'> {
  const tab = opener('about:blank')
  const reply = await post('/api/billing/portal', {}, fetchImpl)
  const url = reply?.status === 200 && typeof reply.body.url === 'string' ? reply.body.url : null
  if (!url) {
    tab?.close()
    return reply?.status === 404 ? 'no_subscription' : reply?.status === 503 ? 'unavailable' : 'unreachable'
  }
  if (tab) {
    tab.opener = null
    tab.location.href = url
  } else {
    window.location.assign(url)
  }
  return 'opened'
}

/**
 * AYNI: the key of a licence for several people bought online (School,
 * Lab), for its buyer to share (`functions/api/billing/key.ts`). Asked
 * for on each click; never kept in the browser.
 */
export async function fetchSharedKey(fetchImpl: typeof fetch = fetch): Promise<{ key: string; seats: number } | 'none' | 'failed'> {
  const reply = await post('/api/billing/key', {}, fetchImpl)
  if (reply?.status === 200 && typeof reply.body.key === 'string') return { key: reply.body.key, seats: Number(reply.body.seats) || 0 }
  return reply?.status === 404 ? 'none' : 'failed'
}
